import { IoMdMusicalNote } from 'react-icons/io';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { LuPlus, LuStretchHorizontal, LuStretchVertical } from 'react-icons/lu';
import { Alert } from '@heroui/alert';
import { Button, ButtonGroup } from '@heroui/button';
import { UseDisclosureReturn } from '@heroui/use-disclosure';
import { PiMetronomeFill } from 'react-icons/pi';

import { useEditSong, useSongMedia } from '@/hooks/useSongbook.ts';
import { Song } from '@/core/db.ts';

import SongMediaItem from './SongMediaItem.tsx';

interface SongMediaProps {
  song: Song;
  editDisclosure: UseDisclosureReturn;
  mediaDisclosure: UseDisclosureReturn;
}

function SongMedia({ song, mediaDisclosure, editDisclosure }: SongMediaProps) {
  const { t } = useTranslation();
  const { isLoading: isMediaLoading, data: media } = useSongMedia(song.id);
  const { mutateAsync: editSong } = useEditSong(song.id);

  if (isMediaLoading) {
    return null;
  }

  if (!media || media.length === 0) {
    return (
      <div className="my-8 p-4 flex justify-center">
        <Alert
          className="sm:max-w-150"
          color="default"
          description={t('songbook.song.noMediaDescription')}
          endContent={
            <Button
              startContent={<LuPlus />}
              variant="solid"
              onPress={mediaDisclosure.onOpen}
            >
              {t('songbook.song.mediaAdd')}
            </Button>
          }
          title={t('songbook.song.noMediaTitle')}
          variant="faded"
        />
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 gap-3 flex items-center">
        <div>
          <ButtonGroup>
            <Button
              isIconOnly
              color={song.orientation === 'horizontal' ? 'secondary' : 'default'}
              onPress={() => editSong({ orientation: 'horizontal' })}
            >
              <LuStretchVertical />
            </Button>
            <Button
              isIconOnly
              color={song.orientation === 'vertical' ? 'secondary' : 'default'}
              onPress={() => editSong({ orientation: 'vertical' })}
            >
              <LuStretchHorizontal />
            </Button>
          </ButtonGroup>
        </div>
        <Button isIconOnly color="primary" onPress={mediaDisclosure.onOpen}>
          <LuPlus />
        </Button>
        <Button
          radius="lg"
          size="sm"
          startContent={<IoMdMusicalNote />}
          variant={song.key ? 'solid' : 'bordered'}
          onPress={editDisclosure.onOpen}
        >
          {song.key || t('songbook.song.keyEditButton')}
        </Button>
        <Button
          radius="lg"
          size="sm"
          startContent={<PiMetronomeFill />}
          variant={song.tempo ? 'solid' : 'bordered'}
          onPress={editDisclosure.onOpen}
        >
          {song.tempo ? t('songbook.song.bpmValueButton', { value: song.tempo }) : t('songbook.song.bpmEditButton')}
        </Button>
      </div>
      <section className={classNames('flex gap-4', { 'flex-col': song.orientation === 'vertical' })}>
        {media.map((mediaFile, index) => (
          <div
            key={mediaFile.id}
            className={classNames(
              song.orientation === 'horizontal' ? {
                'w-2/3': mediaFile.width === '2/3',
                'w-1/2': mediaFile.width === '1/2',
                'w-1/3': mediaFile.width === '1/3',
                'w-1/4': mediaFile.width === '1/4',
                'grow-1': mediaFile.width === 'auto' || !mediaFile.width,
              } : {},
            )}
          >
            <SongMediaItem
              isFirst={index === 0}
              isLast={index === media.length - 1}
              mediaFile={mediaFile}
              song={song}
              onAddFile={mediaDisclosure.onOpen}
            />
          </div>
        ))}
      </section>
    </>
  );
}

export default SongMedia;
